import React, { memo, useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Box, Checkbox, CircularProgress, FormControlLabel, Grid, makeStyles, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import InfoIcon from '../../icons/InfoIcon';
import Switch from '../../atoms/Switch';
import { ColorSwatch } from '../../atoms/evaluation-inputs';
import FieldRenderer from './FieldRenderer';
import { getFieldKey } from '../../services/evaluation-helpers';
import { COLOR_BACKGROUND_WARM, COLOR_BORDER, COLOR_DARK_GRAY, COLOR_LIGHT_GRAY, COLOR_BLUE } from '../../styles/colors';
import { EvaluationTab, PlayerEvaluationSchema } from '../../types/Evaluation';
import { State } from '../../redux/reducers';
import { fetchUserPlayerValidationSetting, updateUserPlayerEvaluationValidationIsAll, updateUserPlayerValidationSetting } from '../../redux/dispatchers/user-settings';

const useStyles = makeStyles(theme => ({
  confidentialBar: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
    backgroundColor: COLOR_BACKGROUND_WARM,
    fontSize: theme.typography.pxToRem(16),
  },
  scoreCard: {
    margin: theme.spacing(2.5),
    padding: theme.spacing(5),
    fontSize: theme.typography.pxToRem(16),
    borderRadius: 12,
    backgroundColor: '#FBFBFB',
    border: `2px solid ${COLOR_BORDER}`,

    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.5),
    },
  },
  scoreCardRow: {
    display: 'flex',
    gap: theme.spacing(1, 2.5),

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  metricBox: {
    flexBasis: 150,

    [theme.breakpoints.down('xs')]: {
      flexBasis: 'auto',
    },
  },
  metricSection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: 50,
    padding: theme.spacing(.5, 2),
    backgroundColor: COLOR_LIGHT_GRAY,
    borderBottom: `2px solid ${COLOR_DARK_GRAY}`,
  },
  metricLabel: {
    fontSize: theme.typography.pxToRem(10),
    lineHeight: 1,
    fontWeight: 600,
    color: COLOR_BLUE,
  },
  metricValue: {
    fontWeight: 600,
  },
  metricValueInput: {
    width: '100%',
    border: 0,
    outline: 'none',
    background: 'transparent',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'inherit',
    fontFamily: 'inherit',
    padding: 0,
    margin: 0,
  },
  lastEditLabel: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(10),
    color: COLOR_DARK_GRAY,
    lineHeight: 1.3,
  },
  scoreColorSwatchWrapper: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  sliderHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sliderInfoIcon: {
    width: 32,
    height: 32,
  },
  sliderControlsInline: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  sliderCompact: {
    display: 'flex',
    alignItems: 'center',
    width: 80,
    margin: theme.spacing(0, 0.5),
  },
  sliderEdgeLabel: {
    color: theme.palette.text.primary,
  },
  sliderLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.primary,
  },
  skipControl: {
    marginTop: 0,
    marginLeft: 0,
  },
  skipLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  fieldSection: {
    padding: theme.spacing(0, 2.5, 2.5),
    fontSize: theme.typography.pxToRem(16),
  },
  fieldRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
  },
}), { name: 'DefaultEvaluationTab' });

interface DefaultEvaluationTabProps {
  schema: PlayerEvaluationSchema;
  defaultTab?: EvaluationTab;
  defaultTabIndex: number;
  control: any;
  setValue: any;
  playerId?: number;
  evalColorPossibleValues?: Record<string, any>;
  getLastEditDateForField: (fieldName?: string) => string | undefined;
}

const DEFAULT_FIELD_ORDER = [
    'userNotes',
    'accountNotes',
    'nilContributed',
    'nilCommitted',
    'scholarshipStatus',
    'eligibilityRemaining',
    // 'valuationPosition',
    'primaryBoardColor',
    'secondaryBoardColor',
] as const;

function DefaultEvaluationTab({
  schema,
  defaultTab,
  defaultTabIndex,
  control,
  setValue,
  playerId,
  evalColorPossibleValues,
  getLastEditDateForField,
}: DefaultEvaluationTabProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isDisplayModeUpdateInFlight, setIsDisplayModeUpdateInFlight] = useState(false);
  const [isSkipValueLoading, setIsSkipValueLoading] = useState(false);
  const [isSkipUpdateInFlight, setIsSkipUpdateInFlight] = useState(false);
  const playerEvaluationValidationIsAll = useSelector(
    (state: State) => state.userSettings.playerEvaluationValidationIsAll,
  );
  const playerDisplayMode = useWatch({
    control,
    name: 'playerDisplayMode',
    defaultValue: playerEvaluationValidationIsAll ? 'all' : 'none',
  });

  const fieldIndexByName = useMemo(() => {
    const entries = (defaultTab?.fields || []).map((field, index) => [field.fieldName || field.name, index] as const);
    return new Map(entries);
  }, [defaultTab]);

  useEffect(() => {
    if (!playerId) return;

    let isActive = true;
    setIsSkipValueLoading(true);

    Promise.resolve((dispatch as any)(fetchUserPlayerValidationSetting(playerId)))
      .then((isChecked: boolean | undefined) => {
        if (!isActive || typeof isChecked !== 'boolean') {
          return;
        }

        setValue('playerDisplaySkip', isChecked, {
          shouldDirty: false,
          shouldTouch: false,
        });
      })
      .finally(() => {
        if (isActive) {
          setIsSkipValueLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [dispatch, playerId, setValue]);

  const isSkipBusy = isSkipValueLoading || isSkipUpdateInFlight;

  const renderDefaultField = (fieldName: string) => {
    if (!defaultTab) return null;

    const fieldIndex = fieldIndexByName.get(fieldName);
    if (fieldIndex === undefined) return null;

    const field = defaultTab.fields[fieldIndex];
    const isColorField = field.fieldName === 'primaryBoardColor' || field.fieldName === 'secondaryBoardColor';
    const gridSize = isColorField ? 6 : field.type === 'TEXT_MULTI' || field.type === 'TEXT' || field.type === 'NUMERIC' ? 12 : 12;

    return (
      <Grid key={field.fieldName || field.name} item xs={12} md={gridSize}>
        <Box className={classes.fieldRow}>
          <FieldRenderer
            name={getFieldKey(defaultTabIndex, fieldIndex)}
            label={field.name}
            type={field.type}
            control={control}
            selectionMessage={field.selectionMessage}
            options={field.options}
            readOnly={field.readOnly}
            possibleValues={field.possibleValues}
            hideValue={isColorField}
            lastEditDate={getLastEditDateForField(field.fieldName)}
          />
        </Box>
      </Grid>
    );
  };

  return (
    <>
      <Box className={classes.confidentialBar}>
        <strong>Confidential Evaluation:</strong> Only viewable by your program
      </Box>

      <Box className={classes.scoreCard}>
        <Box className={classes.scoreCardRow}>
          <Box className={classes.metricBox}>
            <Box className={classes.metricSection}>
              <div className={classes.metricLabel}>Player Evaluation Score</div>
              <div className={classes.metricValue}>
                <Controller
                  name="evalScore"
                  control={control}
                  defaultValue={schema.playerEvaluationScore}
                  render={({ field }) => (
                    <input
                      type="number"
                      className={classes.metricValueInput}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                    />
                  )}
                />
              </div>
            </Box>

            {getLastEditDateForField('evalScore') && (
              <Typography className={classes.lastEditLabel}>
                Last edit : {getLastEditDateForField('evalScore')}
              </Typography>
            )}
          </Box>
          <Box className={classes.metricBox}>
            <Box className={classes.metricSection}>
              <div className={classes.metricLabel}>Player Evaluation Code</div>
              <div className={classes.metricValue}>
                <Controller
                  name="evalCode"
                  control={control}
                  defaultValue={schema.playerEvaluationCode}
                  render={({ field }) => (
                    <input
                      type="text"
                      className={classes.metricValueInput}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                    />
                  )}
                />
              </div>
            </Box>

            {getLastEditDateForField('evalCode') && (
              <Typography className={classes.lastEditLabel}>
                Last edit : {getLastEditDateForField('evalCode')}
              </Typography>
            )}
          </Box>
          <Box className={classes.scoreColorSwatchWrapper}>
            <ColorSwatch
              name="evalColor"
              control={control}
              label="Evaluation Color"
              possibleValues={evalColorPossibleValues}
              hideValue={true}
            />

            {getLastEditDateForField('evalColor') && (
              <Typography className={classes.lastEditLabel}>
                Last edit : {getLastEditDateForField('evalColor')}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={classes.sliderRow}>
          <div className={classes.sliderHeader}>
            <span>Player Display:</span>
            <InfoIcon className={classes.sliderInfoIcon} />
          </div>
          <Box className={classes.sliderControlsInline}>
            <span className={classes.sliderEdgeLabel}>All</span>
            <Box className={classes.sliderCompact}>
              <Controller
                name="playerDisplayMode"
                control={control}
                defaultValue={playerEvaluationValidationIsAll ? 'all' : 'none'}
                render={({ field }) => {
                  const isAllSelected = field.value === 'all';

                  return (
                    <Switch
                      checked={!isAllSelected}
                      onChange={() => {
                        if (isDisplayModeUpdateInFlight) {
                          return;
                        }

                        const nextIsAll = !isAllSelected;
                        field.onChange(nextIsAll ? 'all' : 'none');
                        setIsDisplayModeUpdateInFlight(true);
                        Promise.resolve((dispatch as any)(updateUserPlayerEvaluationValidationIsAll(nextIsAll)))
                          .then(() => {
                            if (!playerId) return;

                            return Promise.resolve((dispatch as any)(fetchUserPlayerValidationSetting(playerId)));
                          })
                          .finally(() => {
                            setIsDisplayModeUpdateInFlight(false);
                          });
                      }}
                    />
                  );
                }}
              />
            </Box>
            <span className={classes.sliderEdgeLabel}>None</span>
            {isDisplayModeUpdateInFlight && <CircularProgress size={12} thickness={6} />}
            <Controller
              name="playerDisplaySkip"
              control={control}
              defaultValue={schema.playerDisplay === 'skip'}
              render={({ field }) => (
                <FormControlLabel
                  className={classes.skipControl}
                  control={(
                    <Checkbox
                      color="primary"
                      checked={playerDisplayMode === 'none' ? false : Boolean(field.value)}
                      onChange={event => {
                        if (isSkipBusy) {
                          return;
                        }

                        const isChecked = event.target.checked;
                        field.onChange(isChecked);

                        if (playerId) {
                          setIsSkipUpdateInFlight(true);
                          Promise.resolve((dispatch as any)(updateUserPlayerValidationSetting(playerId, isChecked)))
                            .finally(() => {
                              setIsSkipUpdateInFlight(false);
                            });
                        }
                      }}
                      disabled={playerDisplayMode === 'none' || isSkipBusy}
                    />
                  )}
                  label={(
                    <span className={classes.skipLabel}>
                      <span>Skip</span>
                      {isSkipBusy && <CircularProgress size={12} thickness={6} />}
                    </span>
                  )}
                />
              )}
            />
          </Box>
        </Box>
      </Box>

      <Box className={classes.fieldSection}>
        <Grid container spacing={3}>
          {DEFAULT_FIELD_ORDER.map(renderDefaultField)}
        </Grid>
      </Box>
    </>
  );
}

export default memo(DefaultEvaluationTab);
